import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  Pagination,
  TableCell,
  TableFooter,
  TableRow,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import { getAppointmentsTableReportData } from "../../../../redux/reducers/reportSlice";
import { getClinicVets } from "../../../../redux/reducers/vetSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import { serviceTypes, typeList } from "../../../../util/dropList";
import { minutesToHours } from "../../../../util/function";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";

const tableHdrs = [
  "appointmentDateTime",
  "vetName",
  "petName",
  "caseId",
  "typeOfConsult",
  "serviceType",
  "timeSpent",
  "bookingSource",
  "assignedUsers",
];
const initSearch = {
  vetName: "",
  petName: "",
  caseId: "",
  typeOfConsult: [],
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};
const filterByList = [
  "Appointment Date̦",
  "Service Type",
  "Time Spent",
  "Booking Source",
  "Assigned Users",
];
const dropFields = ["Service Type", "Booking Source", "Assigned Users"];

const AppointmentsTabTableReport = () => {
  const dispatch = useDispatch();
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { appointmentTable } = useSelector((state) => state?.report);
  const vets = useSelector((state) => state?.vet?.vets);
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(getClinicVets("?type=all"));
  }, [dispatch]);

  useEffect(() => {
    onApiCall(1);
  }, [
    searchValues?.vetName,
    searchValues?.petName,
    searchValues?.caseId,
    searchValues?.typeOfConsult,
  ]);

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = {
      type: "appointments",
      page: currentPage,
      itemSize: rowsPerPage,
    };
    if (reset) return reqObj;
    if (searchValues?.vetName?.length > 0) {
      reqObj.vetName = [searchValues?.vetName];
    }
    if (searchValues?.petName?.length > 0) {
      reqObj.petName = [searchValues?.petName];
    }
    if (searchValues?.caseId?.length > 0) {
      reqObj.caseId = [searchValues?.caseId];
    }
    if (searchValues?.typeOfConsult?.length > 0) {
      reqObj.typeOfConsult = searchValues?.typeOfConsult?.map(
        (li) => li?.value,
      );
    }

    if (filterValues?.selectedList?.includes("Appointment Date")) {
      reqObj.startDate = moment(
        new Date(filterValues?.values?.["Start Date"] ?? new Date()),
      ).format("YYYY-MM-DD");
      reqObj.endDate = moment(
        new Date(filterValues?.values?.["End Date"] ?? new Date()),
      ).format("YYYY-MM-DD");
    }
    if (filterValues?.selectedList?.includes("Time Spent")) {
      reqObj.minTimeSpentMinutes = Number(
        filterValues?.values?.["Min Time Spent"] ?? 0,
      );
      reqObj.maxTimeSpentMinutes = Number(
        filterValues?.values?.["Max Time Spent"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Service Type")) {
      reqObj.serviceType = filterValues?.values?.["Service Type"]?.map(
        (li) => li?.value,
      );
    }
    if (filterValues?.selectedList?.includes("Booking Source")) {
      reqObj.bookingSource = filterValues?.values?.["Booking Source"]?.map(
        (li) => li?.value,
      );
    }
    if (filterValues?.selectedList?.includes("Assigned Users")) {
      reqObj.assignedUsers = (
        filterValues?.values?.["Assigned Users"] ?? []
      )?.map((li) => li?.value);
    }
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    dispatch(getAppointmentsTableReportData(getFilterObj(pge, reset)));
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find(
        (tblHdr) => tblHdr?.name === pd,
      );
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => {
    setEditColModVsble(false);
  };

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders
      ?.filter((act) => act?.isSelected)
      ?.map((act) => {
        return act?.name;
      });
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil(
    (appointmentTable?.totalRecords ?? 0) / rowsPerPage,
  );

  const handleChangePage = (e, selectedPage) => {
    onApiCall(selectedPage);
  };

  const getTableData = () => {
    return (appointmentTable?.data ?? [])?.map((li) => ({
      ...li,
      typeOfConsult:
        li?.typeOfConsult === "Home" ? "Home Visit" : li?.typeOfConsult,
      appointmentDateTime:
        li?.appointmentDateTime?.length > 0
          ? moment(new Date(li?.appointmentDateTime)).format(
              "DD MMM YYYY, HH:mm",
            )
          : null,
      bookingSource:
        li?.bookingSource?.length > 0 ? li?.bookingSource?.join(", ") : null,
    }));
  };

  const handleOpenChart = () => {
    console.log("Log triggered");
  };

  const getVetList = () => {
    const reqList = (appointmentTable?.data ?? [])
      ?.map((li) => li?.vetName)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    // const reqDropList = uniqList?.map((li) => ({ name: li, valeu: li }));
    return uniqList;
  };

  const getPetList = () => {
    const reqList = (appointmentTable?.data ?? [])
      ?.map((li) => li?.petName)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    // const reqDropList = uniqList?.map((li) => ({ name: li, valeu: li }));
    return uniqList;
  };

  const getCaseIds = () => {
    const reqList = (appointmentTable?.data ?? [])
      ?.map((li) => li?.caseId?.toString())
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    // const reqDropList = uniqList?.map((li) => ({ name: li, valeu: li }));
    return uniqList;
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({
      ...searchValues,
      [name?.trim()?.replace(/,$/, "")]: value, // remove spaces & trailing commas
    });
  };

  const onFilterChange = (name, value) => {
    const reqObj = { ...filterValues, [name]: value };
    setFilterValues(reqObj);
  };

  const onReset = () => {
    flushSync(() => {
      setSearchValues(initSearch);
    });
    flushSync(() => {
      setFilterValues({ ...initFilter, selectedList: [], values: {} });
    });
    onApiCall(1, true);
  };

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    const reqObj = {
      ...filterValues,
      values: {
        ...filterValues?.values,
        [name?.trim()?.replace(/,$/, "")]: value,
      },
    };
    setFilterValues(reqObj);
  };

  const onFilterAdd = (value) => {
    const reqObj = { ...filterValues, added: value };
    setFilterValues(reqObj);
  };

  const onApplyFilter = () => {
    onApiCall();
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const onFilterReset = () => {
    const reqObj = {
      ...initFilter,
      visible: true,
      selectedList: [],
      values: {},
    };
    setFilterValues(reqObj);
  };

  const onCloseFilter = () => {
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) {
      reqList = reqList?.filter((item) => item !== name);
    } else {
      reqList.push(name);
    }
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const getFieldName = (name, type) => {
    if (name === "Appointment Date̦") {
      if (type === "start") return "Start Date";
      return "End Date";
    }
    if (name === "Time Spent") {
      if (type === "start") return "Min Time Spent";
      return "Max Time Spent";
    }
  };

  const getBookingSource = () => {
    const reqList = (appointmentTable?.data ?? [])
      ?.flatMap((li) => li?.bookingSource)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    const reqDropList = uniqList?.map((li) => ({ name: li, value: li }));
    return reqDropList;
  };

  const getAssignedUsers = (users = []) => {
    if (users?.length === 0 || !users) return "";
    const reqList = users?.map((li) => {
      const reqList = vets?.vets?.find((vl) => vl?.doctorId === li);
      return reqList?.name ?? "";
    });
    return reqList?.join(", ");
  };

  const getAssignedUserList = () => {
    if (!appointmentTable?.data) return [];
    const reqList = (appointmentTable?.data ?? [])
      ?.flatMap((li) => li?.assignedUsers)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    const reqDropList = uniqList?.filter(Boolean)?.map((li) => {
      const reqList = vets?.vets?.find((vl) => vl?.doctorId === li);
      return { name: reqList?.name, value: li };
    });
    return reqDropList;
  };

  const getDropListByType = (typ) => {
    return typ === "Assigned Users"
      ? getAssignedUserList()
      : typ === "Service Type"
        ? serviceTypes
        : getBookingSource();
  };

  return (
    <>
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              options={getVetList()}
              value={searchValues?.vetName ?? []}
              name="vetName"
              handleChange={onSearchChange}
              label="Vet Name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
              // multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              options={getPetList()}
              value={searchValues?.petName ?? []}
              name="petName"
              handleChange={onSearchChange}
              label="Pet Name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
              // multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              options={getCaseIds()}
              value={searchValues?.caseId ?? []}
              name="caseId"
              handleChange={onSearchChange}
              label="Case ID"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
              // multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={typeList}
              value={searchValues?.typeOfConsult ?? []}
              name="typeOfConsult"
              handleChange={onSearchChange}
              label="Consultation Mode"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0
                ? `${filterValues?.selectedList?.length} `
                : ""}
              Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleOpenChart}
          >
            <AutoGraphIcon sx={w20h20BlueIcon} />
          </div>
        </div>
      </div>
      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {appointmentTable?.totalRecords ?? 0}
      </Typography>
      <div>
        <ReportTable
          datas={getTableData()?.map((li) => ({
            ...li,
            timeSpent:
              (li?.timeSpentMinutes ?? "")?.toString()?.length > 0
                ? minutesToHours(li?.timeSpentMinutes)
                : "",
            bookingSource: (li?.bookingSource ?? [])?.toString(),
            assignedUsers: getAssignedUsers(li?.assignedUsers),
          }))}
          columns={crntTblHeders}
        />
        <div className="flex1-end">
          {Math.ceil((appointmentTable?.totalRecords ?? 0) / rowsPerPage) >
          1 ? (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={12}>
                  <div className="flex1-end">
                    <Pagination
                      count={totalPage}
                      variant="outlined"
                      color="primary"
                      page={page}
                      onChange={handleChangePage}
                    />
                  </div>
                </TableCell>
              </TableRow>
            </TableFooter>
          ) : null}
        </div>
      </div>

      <CustomModal
        open={editColModVsble}
        onClose={onEditModClose}
        header="Columns"
        modal
        modalWidth={25}
      >
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <div>
              <CustomButton
                text="Save"
                smallBtn
                onClick={handleSaveTableColumn}
              />
            </div>
          </div>
        </div>
      </CustomModal>
      <CustomModal
        open={filterValues?.visible}
        onClose={onCloseFilter}
        header="Filter"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography className="gray38 fs12 txt-regular">
                Filter by:
              </Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <div>
                      <CustomCheckbox
                        checked={filterValues?.selectedList?.includes(li)}
                        onChange={() => onSelectFilter(li)}
                      />
                    </div>
                    <Typography className="txt-regular fs14 black mb5 mlMin15">
                      {li}
                    </Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div
                    className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor"
                    onClick={() => onFilterAdd(false)}
                  >
                    <AddIcon
                      sx={{ color: AppColors.appPrimary, fontSize: 15 }}
                    />
                    <Typography className="font-medium fs12 blue-color ml5">
                      Add Filter
                    </Typography>
                  </div>
                  <div>
                    {filterValues?.selectedList?.map((li) => (
                      <div className="mt20">
                        {dropFields?.includes(li) ? (
                          <CustomSelect
                            list={getDropListByType(li)}
                            value={filterValues?.values?.[li] ?? []}
                            name={li}
                            handleChange={onFilterValuesChange}
                            label={li}
                            multiSelectSearchCheck
                          />
                        ) : li === "Appointment Date̦" || li === "Time Spent" ? (
                          <div className="flex-row-between-align-center">
                            <div className="w45Per">
                              <CustomTextField
                                placeholder={getFieldName(li, "start")}
                                fullWidth
                                name={getFieldName(li, "start")}
                                handleChange={onFilterValuesChange}
                                value={
                                  filterValues?.values?.[
                                    getFieldName(li, "start")
                                  ] ?? ""
                                }
                                label={getFieldName(li, "start")}
                                mobileDate={li === "Appointment Date̦"}
                                type="number"
                              />
                            </div>
                            <div className="w45Per">
                              <CustomTextField
                                placeholder={getFieldName(li, "end")}
                                fullWidth
                                name={getFieldName(li, "end")}
                                handleChange={onFilterValuesChange}
                                value={
                                  filterValues?.values?.[
                                    getFieldName(li, "end")
                                  ] ?? ""
                                }
                                label={getFieldName(li, "end")}
                                mobileDate={li === "Appointment Date̦"}
                                type="number"
                              />
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <div>
                    <CustomButton
                      text="Add"
                      smallBtn
                      onClick={() => onFilterAdd(true)}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <div>
                    <CustomButton
                      text="Reset All"
                      smallBtn
                      onClick={onFilterReset}
                      tabSelectdBtn
                    />
                  </div>
                  <div className="ml20">
                    <CustomButton
                      text="Apply Filters"
                      smallBtn
                      onClick={onApplyFilter}
                    />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </>
  );
};

export default AppointmentsTabTableReport;
